/**
 * State-aware SPA routing derivations — R2.
 *
 * Single derivation point between the state core (lib/stateCore.mjs) and the
 * router/read surfaces. Everything here is a PURE function of a states array
 * plus app-level instances bound to LAUNCHED_STATES, so:
 *   - while LAUNCHED_STATES === ['nsw'], every derived path/pattern is
 *     byte-identical to the former hardcoded '/nsw/...' literals;
 *   - adding a slug to LAUNCHED_STATES (in lib/stateCore.mjs) is the single
 *     switch that makes that state's tree routable — no edits here or in
 *     App.tsx;
 *   - tests can exercise the derivation with hypothetical states without
 *     mutating the core's real LAUNCHED_STATES.
 *
 * Legacy NSW-only routes (/gyms/:suburb/:slug redirect tree, singular
 * /nsw/:location/gym/:id) are FROZEN in App.tsx and deliberately not derived
 * here.
 */

import { LAUNCHED_STATES, DEFAULT_STATE, stateSlugToDisplay, buildStateHubUrl } from './stateCore.mjs';
import type { StateSlug } from './stateCore.mjs';

/** 'NSW' — display form of the default state ('nsw'). */
export const DEFAULT_STATE_DISPLAY = stateSlugToDisplay(DEFAULT_STATE);

/**
 * '/nsw/suburbs' — the default state's suburbs hub path (Header nav, landing links; UI-57: was '/nsw/gyms').
 * Derived from buildStateHubUrl so this and the breadcrumb spine's tier-2 href
 * cannot drift apart.
 */
export const DEFAULT_STATE_HUB_PATH = buildStateHubUrl(DEFAULT_STATE);

/**
 * Every launched state's suburbs hub, as {slug, path, label} (UI-09).
 *
 * The chrome (Header dropdown, mobile drawer, Footer) renders THIS list rather
 * than DEFAULT_STATE_HUB_PATH, which pinned every surface to the first state:
 * on all 4,223 pages the only directory entry was "NSW Suburbs", so Victoria's
 * hub was reachable only via NSW's. Derived from LAUNCHED_STATES, so launching
 * a third state adds its entry to all three surfaces with no edit here or in
 * any component.
 *
 * Label form ("NSW Suburbs") is the vocabulary the Header already used — this
 * generalises the existing string, it does not author new wording.
 */
export const STATE_HUBS: ReadonlyArray<{ slug: StateSlug; path: string; label: string }> =
    LAUNCHED_STATES.map((state) => ({
        slug: state,
        path: buildStateHubUrl(state),
        label: `${stateSlugToDisplay(state)} Suburbs`,
    }));

/**
 * Full state names, for surfaces that spell the state out rather than abbreviate it
 * (the footer's column headings). Keyed by slug over the WHOLE StateSlug union, not
 * just the launched ones, so launching a state cannot reach a missing name.
 *
 * ⚠️ WHY THIS LIVES HERE AND NOT WHERE THE OTHER COPY OF IT LIVES: `lib/stateHubIntro.ts`
 * carries its own nsw/vic name map. That module is the state-hub INTRO COPY, which the
 * operator has ruled for removal (2026-08-16) — so a chrome surface importing names from
 * it would break when the copy goes. Names are structural; copy is not. They are separated
 * here deliberately, and the sibling map may disappear without touching the footer.
 */
const STATE_FULL_NAME: Readonly<Record<StateSlug, string>> = {
    nsw: 'New South Wales',
    vic: 'Victoria',
    qld: 'Queensland',
    sa: 'South Australia',
    wa: 'Western Australia',
    tas: 'Tasmania',
    nt: 'Northern Territory',
    act: 'Australian Capital Territory',
};

/**
 * ⭐ Per-state NAVIGATION GROUPS — one per launched state, each holding that state's two
 * destinations. The single source for BOTH the Header menus and the Footer columns
 * (operator-ruled 2026-08-16: the chrome presents "NSW" and "VIC" separately rather than
 * one combined "Gyms" menu).
 *
 * ⚠️ WHAT THIS EXISTS TO PROTECT, because the obvious implementation destroys it. The
 * operator asked for two menus, "NSW" and "VIC". Writing two hand-authored menus would
 * satisfy that sentence and silently delete UI-09's property: that launching a third state
 * adds it to every chrome surface with NO component edit. Deriving the GROUPS from
 * LAUNCHED_STATES keeps the requested shape and the property — state three gets its own
 * menu and its own footer column automatically.
 *
 * ⚠️ AND THE LIMIT, stated rather than discovered later: this scales the CHROME, not the
 * LAYOUT. Each launched state costs one top-level header item and one footer column, so
 * beyond about four states the header bar and the footer grid will need a different shape.
 * That is a layout decision for whoever launches the fourth state; nothing here will break,
 * it will merely look crowded.
 *
 * Every href is built by the canonical builders, never typed, so a URL-format ruling still
 * propagates from one place.
 */
export const STATE_NAV_GROUPS: ReadonlyArray<{
    slug: StateSlug;
    /** "NSW" — the header's menu label. */
    short: string;
    /** "New South Wales" — the footer's column heading. */
    full: string;
    links: ReadonlyArray<{ id: string; label: string; to: string }>;
}> = LAUNCHED_STATES.map((state) => {
    const short = stateSlugToDisplay(state);
    return {
        slug: state,
        short,
        full: STATE_FULL_NAME[state],
        links: [
            { id: `${state}-gyms`, label: `${short} Gyms`, to: buildStateDirectoryRootUrl(state) },
            { id: `${state}-suburbs`, label: `${short} Suburbs`, to: buildStateHubUrl(state) },
        ],
    };
});

/**
 * Per-state default map centre (UI-25) — the view a directory map opens on
 * before any search resolves a real centre.
 *
 * Previously "Sydney" was hardcoded in TWO independent copies (GoogleMapView
 * and DirectoryPage each declared their own NSW_CENTER), so every Victorian
 * page's initial map showed Sydney, and the duplicates could drift. One table,
 * consumed via the route state by both.
 *
 * Partial<> because StateSlug is the full 8-state union: a NEW launched state
 * must AUTHOR its centre (this is a decision, not derivable data — the same
 * reasoning as functions' STATE_GEOFENCE_KM). lib/stateHubs.test.ts locks
 * "every launched state has an authored centre" as a launch-checklist gate.
 */
export const STATE_MAP_CENTERS: Partial<Record<StateSlug, { lat: number; lng: number }>> = {
    nsw: { lat: -33.8688, lng: 151.2093 }, // Sydney
    vic: { lat: -37.8136, lng: 144.9631 }, // Melbourne
};

/** Metro-scale default zoom shared by every state's initial map view. */
export const DEFAULT_MAP_ZOOM = 8;

/** The state's authored centre, falling back to the default state's (never undefined). */
export function getStateMapCenter(state: StateSlug): { lat: number; lng: number } {
    return STATE_MAP_CENTERS[state] ?? STATE_MAP_CENTERS[DEFAULT_STATE]!;
}

/** Route path patterns for one launched state's directory/profile tree. */
export interface StateRoutePaths {
    state: StateSlug;
    /** Static SEO hub (the suburb A–Z index), e.g. '/nsw/suburbs' (UI-57 flip: was '/nsw/gyms'). */
    hub: string;
    /** Suburb directory, e.g. '/nsw/:suburb/gyms'. */
    directory: string;
    /** Shared gym-profile / style-filter segment, e.g. '/nsw/:suburb/gyms/:gymSlugWithId'. */
    segment: string;
    // ⚰️ `stateStyle: string` ('/{state}/gyms/:style') was here — UI-73, removed by UI-76.
    // See the tombstone above deriveStateRoutePaths' helpers. The exact-shape assertion in
    // lib/stateRoutes.test.ts is what forces anyone adding a route class to state it here.
}

/** Derive the per-state route path patterns for a states array (pure). */
export function deriveStateRoutePaths(states: readonly StateSlug[]): StateRoutePaths[] {
    return states.map((state) => ({
        state,
        // UI-57 flip: the index vacated '/{state}/gyms' (now the state DIRECTORY root,
        // registered separately in App.tsx via buildStateDirectoryRootUrl).
        hub: `/${state}/suburbs`,
        directory: `/${state}/:suburb/gyms`,
        segment: `/${state}/:suburb/gyms/:gymSlugWithId`,
    }));
}

/** App instance: route paths for every launched state. */
export const STATE_ROUTE_PATHS = deriveStateRoutePaths(LAUNCHED_STATES);

/**
 * Build the isDirectoryRoute predicate for a states array (pure).
 * Any launched state's namespace counts as directory space except its static
 * hub page — the suburb A–Z at '/{state}/suburbs' (UI-57 flip: the exclusion
 * followed the index when it vacated '/{state}/gyms', which is now the state
 * DIRECTORY root and therefore directory space by this very predicate).
 */
export function makeIsStateDirectoryPath(states: readonly StateSlug[]): (pathname: string) => boolean {
    const prefixes = states.map((s) => `/${s}/`);
    const hubs = new Set(states.map((s) => `/${s}/suburbs`));
    return (pathname: string) => prefixes.some((p) => pathname.startsWith(p)) && !hubs.has(pathname);
}

/** App instance bound to LAUNCHED_STATES. */
export const isStateDirectoryPath = makeIsStateDirectoryPath(LAUNCHED_STATES);

/**
 * '/{state}/gyms' — a state's DIRECTORY ROOT (the suburb-less, state-scoped search
 * page ruled in UI-57). This address is STABLE ACROSS THE FLIP: it was the A–Z
 * hub's address; the directory took it and the hub moved to buildStateHubUrl's
 * value. Links built from this never need to change.
 * ⚠️ DELIBERATE cross-boundary twin: lib/stateCore.mjs exports the same builder
 * for the generator scripts (plain-Node .mjs cannot import TS). Each docblock
 * points at the other; change BOTH or the manifest and the router disagree.
 */
export function buildStateDirectoryRootUrl(state: StateSlug): string {
    return `/${state}/gyms`;
}

/**
 * Which launched state's DIRECTORY ROOT is this pathname — or null?
 *
 * ⚠️ LIVE since the UI-57 flip (2026-08-14): buildStateHubUrl now returns
 * '/{state}/suburbs', so a root candidate ('/{state}/gyms') is no longer the hub
 * and the app instance maps it to its state. It was DORMANT BY CONSTRUCTION from
 * the feature slice (2026-08-13) until the flip armed it — the arming edit
 * (buildStateHubUrl) was sequenced LAST because hub knowledge lives in three
 * places that had to move together: (1) makeIsStateDirectoryPath above,
 * (2) deriveStateRoutePaths' `hub:` field (App.tsx's route table), (3) this
 * matcher's default hubUrlFor. Register 2026-08-13 (+50)/(+53) holds the full
 * coordinated-edit record; lib/stateRoutes.stateDirectoryRoot.test.ts carries the
 * inverted dormancy suite (the pre-flip hub injected puts it back to sleep — the
 * §3.4 control, flipped with the config).
 */
export function makeStateDirectoryRootMatcher(
    states: readonly StateSlug[],
    hubUrlFor: (state: StateSlug) => string = buildStateHubUrl,
): (pathname: string) => StateSlug | null {
    const roots = new Map<string, StateSlug>(states.map((s) => [buildStateDirectoryRootUrl(s), s]));
    const hubs = new Set(states.map((s) => hubUrlFor(s)));
    return (pathname: string) => {
        const state = roots.get(pathname);
        return state !== undefined && !hubs.has(pathname) ? state : null;
    };
}

/** App instance bound to LAUNCHED_STATES and the real hub URLs. */
export const stateDirectoryRootSlug = makeStateDirectoryRootMatcher(LAUNCHED_STATES);

/**
 * ⚰️ TOMBSTONE — UI-73's state-level style route helpers lived here and are GONE (UI-76,
 * 2026-08-16). `makeStateStyleMatcher`, `stateStyleRoute` and `buildStateStyleUrl` matched and
 * built `/{state}/gyms/{style}`; the `stateStyle` field on StateRoutePaths went with them.
 *
 * ⚠️ REMOVED BY OPERATOR RULING, NOT BECAUSE THE CODE WAS WRONG — and the distinction matters
 * to whoever finds this. Both lanes built, gated, mutation-proved and shipped the feature; it
 * was live in deploy #26. **The PREMISE was mistaken and only an operator could have known it:
 * these urls were never intended to be pages at all** — the intent was a redirect to the
 * style-filtered directory. Item 48's SEO half is closed WON'T-DO, and D&P's data join (A-x),
 * which would have put gyms on them, is cancelled with it.
 *
 * ⭐ The reversal was free because the pages shipped `noindex`, unsitemapped and with zero
 * inbound links across 4,236 pages, so no search engine ever saw them.
 *
 * DO NOT re-add these helpers to "restore consistency" with the build side: D&P removed
 * `STATE_STYLE_ROUTE_RE` and the `state-style` shape from `lib/ssgRouteClassifiers.mjs` in the
 * same change. Absence on both sides is the intended state and is locked by
 * `lib/stateStyleRoutes.test.ts`.
 */

/**
 * Where should a generic "Search Gyms / View map" control take THIS page's visitor?
 * A pathname carrying a launched state goes to that state's directory root — the
 * visitor's state is already known from the address, and sending them to the
 * chooser to re-answer it is a lost click (review-caught, 2026-08-14). A genuinely
 * state-less pathname (home, blog, favourites…) goes to the '/gyms' chooser, which
 * exists precisely for unknown-state intent. The triggerFilters router flag stays
 * meaningful on directory targets and is inert on the chooser (documented at its
 * consumer in DirectoryPage).
 */
export function searchSurfaceForPathname(pathname: string): string {
    const parts = pathname.split('/');
    const idx = findLaunchedStateIndex(parts);
    return idx >= 0 ? buildStateDirectoryRootUrl(parts[idx] as StateSlug) : '/gyms';
}

/**
 * The suburb slug of a directory pathname — or '' when the path carries none.
 *
 * A suburb exists ONLY in the canonical '/{state}/:suburb/gyms…' shape: the segment
 * AFTER the candidate must be 'gyms'. This is the single shared parse for the three
 * former inline copies (useDirectoryFilters ×2, useDirectorySearch's hold-fire
 * replica), which all read pathParts[stateIdx + 1] unconditionally — so a state
 * DIRECTORY ROOT ('/{state}/gyms') parsed its own 'gyms' segment as a suburb named
 * "Gyms" and would have auto-searched it at the moment of the UI-57 flip. FATAL,
 * review-caught before it could ship. Two shapes this rule also settles, in the
 * safe direction: '/{state}/gyms/' (trailing slash) and unrouted '/{state}/anything'
 * both parse to '' — no phantom suburb search on a path the router never built.
 */
export function suburbSlugFromPathParts(pathParts: readonly string[]): string {
    const idx = findLaunchedStateIndex(pathParts);
    if (idx < 0) return '';
    const candidate = pathParts[idx + 1];
    return candidate && pathParts[idx + 2] === 'gyms' ? candidate : '';
}

/**
 * Build the canonical gym-profile pathname regex for a states array (pure).
 * Equivalent (for ['nsw']) to the former literal
 *   /^\/nsw\/[^/]+\/gyms\/[^/]+-g[A-Za-z0-9]+$/
 * The -g[A-Za-z0-9]+ discriminator is FROZEN verbatim (R2 locked decision 3).
 * Non-capturing state group — no capture-group numbering impact.
 */
export function makeCanonicalProfileRe(states: readonly StateSlug[]): RegExp {
    return new RegExp(`^/(?:${states.join('|')})/[^/]+/gyms/[^/]+-g[A-Za-z0-9]+$`);
}

/** App instance bound to LAUNCHED_STATES. */
export const CANONICAL_PROFILE_RE = makeCanonicalProfileRe(LAUNCHED_STATES);

/**
 * Index of the first path segment that is a launched state slug, or -1.
 * Equivalent (for ['nsw']) to parts.indexOf('nsw') — both return the first
 * matching segment's index. Used by URL→filter parsing.
 */
export function findLaunchedStateIndex(pathParts: readonly string[]): number {
    return pathParts.findIndex((part) => (LAUNCHED_STATES as readonly string[]).includes(part));
}

/**
 * Launched states that have NO route in `routes` — empty means full coverage.
 *
 * WHY THIS EXISTS, and it is the half of the launch fix that outlives the fix.
 * scripts/ssg-routes.json is the manifest vite-react-ssg pre-renders from, and it is
 * the ONE generated artefact written WHOLESALE rather than merged
 * (scripts/generate-ssg-routes.mjs says so in its own header). So a run that targets a
 * SUBSET of the launched states — the no-flag default, an explicit `--state <slug>`, or
 * simply a committed manifest that predates a launch — silently produces a manifest with
 * a launched state missing, and NOTHING downstream notices: the build pre-renders what it
 * is given, exits 0, and the sitemap generator, the hub switcher and the redirect table
 * still derive from LAUNCHED_STATES. The result is a launched state with zero pages, and
 * links and sitemaps pointing at them. Measured in a flipped sandbox: 2,323 routes emitted,
 * 0 of them for the second state, rc=0 at every step, artefacts byte-identical to the
 * previous build's.
 *
 * A state's routes are identified by the FIRST PATH SEGMENT — the same derivation
 * vite.config.ts's detectRouteType uses (`route.split('/')[1]`) and the same one
 * findLaunchedStateIndex above is built on — so this cannot disagree with the classifier
 * about which state a route belongs to.
 *
 * Pure, and takes both arguments, so the two-state case is testable WITHOUT mutating the
 * core's real LAUNCHED_STATES — the same pure/bound split every other derivation in this
 * file uses. The caller (vite.config.ts includedRoutes) supplies LAUNCHED_STATES and
 * throws; deciding what to do about a gap is not this function's business.
 *
 * Routes with no state segment (the global statics '/', '/gyms', '/about', and the blog
 * tree) simply cover no state and are ignored, which is correct: they are not evidence
 * that any state was generated.
 */
export function launchedStatesMissingFromRoutes(
    routes: readonly string[],
    states: readonly string[],
): string[] {
    const covered = new Set(routes.map((r) => r.split('/')[1] ?? ''));
    return states.filter((s) => !covered.has(s));
}
